/**
 * Broadcast door copy — EN + AR (MENA business tone).
 * House shape: every string is `{ en, ar }`, resolved through `useLocale()`.
 */

export type BroadcastLocale = 'en' | 'ar'

export type LocalizedString = { en: string; ar: string }

export function at(entry: LocalizedString, locale: BroadcastLocale): string {
  return entry[locale] || entry.en
}

export function t(entry: LocalizedString, locale: BroadcastLocale, vars?: Record<string, string | number>): string {
  let value = at(entry, locale)
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      value = value.split(`{${k}}`).join(String(v))
    }
  }
  return value
}

export const BROADCAST_COPY = {
  'door.title': { en: 'Broadcast listing', ar: 'بثّ العقار' },
  'door.description': {
    en: 'No campaign or strategy is attached to this listing yet. Choose how to broadcast it. Everything is staged as a draft — launch wiring ships after strategy F6.',
    ar: 'لا يوجد حملة أو استراتيجية مرتبطة بهذا العقار بعد. اختر طريقة البث. كل شيء يُحفظ كمسودة — التفعيل يأتي بعد اكتمال استراتيجية F6.',
  },
  'door.objective.label': { en: 'Objective', ar: 'الهدف' },
  'door.objective.placeholder': {
    en: 'e.g. Sell the Mar Mikhael loft this month',
    ar: 'مثال: بيع شقة مار مخايل هذا الشهر',
  },
  'door.mode.paid.label': { en: 'Paid', ar: 'مدفوع' },
  'door.mode.paid.hint': {
    en: 'Meta ad — you define the spend, objective and targeting.',
    ar: 'إعلان ميتا — تحدد أنت الميزانية والهدف والاستهداف.',
  },
  'door.mode.social.label': { en: 'Social', ar: 'اجتماعي' },
  'door.mode.social.hint': {
    en: 'Your existing social channels — continues in the publish flow.',
    ar: 'قنواتك الاجتماعية الحالية — يُكمل في مسار النشر.',
  },
  'door.mode.both.label': { en: 'Both', ar: 'كلاهما' },
  'door.mode.both.hint': {
    en: 'One campaign with a paid side and a social side, split your way.',
    ar: 'حملة واحدة بجانب مدفوع وآخر اجتماعي، بالتقسيم الذي تختاره.',
  },

  'paid.budget.label': { en: 'Spend', ar: 'الميزانية' },
  'paid.budget.capsHelper': {
    en: 'For {country}, spend must be between {min} and {max}.',
    ar: 'بالنسبة إلى {country}، يجب أن تكون الميزانية بين {min} و{max}.',
  },
  'paid.budget.capsMinOnly': {
    en: 'For {country}, spend must be at least {min}.',
    ar: 'بالنسبة إلى {country}، يجب ألا تقل الميزانية عن {min}.',
  },
  'paid.budget.capsMaxOnly': {
    en: 'For {country}, spend must be at most {max}.',
    ar: 'بالنسبة إلى {country}، يجب ألا تتجاوز الميزانية {max}.',
  },
  'paid.targeting.country.label': { en: 'Country', ar: 'الدولة' },
  'paid.targeting.country.helper': {
    en: 'Locked to your market ({country}).',
    ar: 'مثبّت على سوقك ({country}).',
  },
  'paid.targeting.areas.label': { en: 'Areas (optional)', ar: 'المناطق (اختياري)' },
  'paid.targeting.areas.placeholder': {
    en: 'Comma-separated area ids, e.g. beirut_hamra',
    ar: 'معرّفات المناطق مفصولة بفواصل، مثال: beirut_hamra',
  },
  'paid.targeting.radius.label': {
    en: 'Radius around areas, meters (optional)',
    ar: 'نطاق حول المناطق، بالمتر (اختياري)',
  },

  'both.total.label': { en: 'Total spend (paid + social)', ar: 'الميزانية الإجمالية (مدفوع + اجتماعي)' },
  'both.split.title': { en: 'How should the budget split?', ar: 'كيف تريد تقسيم الميزانية؟' },
  'both.split.predefined.label': { en: 'Use a predefined split', ar: 'استخدام تقسيم جاهز' },
  'both.split.predefined.empty': {
    en: 'No predefined splits for your market yet.',
    ar: 'لا توجد تقسيمات جاهزة لسوقك بعد.',
  },
  'both.split.ai.label': { en: 'Let AI propose', ar: 'دع الذكاء الاصطناعي يقترح' },
  'both.split.ai.helper': {
    en: 'The AI proposes based on your listing and your market. Review and edit — nothing is created until you confirm.',
    ar: 'يقترح الذكاء الاصطناعي بناءً على عقارك وسوقك. راجع المقترح وعدّله — لن يُنشأ شيء قبل تأكيدك.',
  },
  'both.split.ai.propose': { en: 'Propose with AI', ar: 'اقترح بالذكاء الاصطناعي' },
  'both.split.ai.proposing': { en: 'Asking the AI…', ar: 'جارٍ سؤال الذكاء الاصطناعي…' },
  'both.split.ai.unavailable': {
    en: 'AI proposals are not available right now ({code}). Pick a predefined split or type one yourself.',
    ar: 'مقترحات الذكاء الاصطناعي غير متاحة حالياً ({code}). اختر تقسيماً جاهزاً أو أدخله يدوياً.',
  },
  'both.split.ai.proposedTitle': { en: 'Confirm the AI proposal', ar: 'أكّد مقترح الذكاء الاصطناعي' },
  'both.split.manual.label': { en: 'Type the split myself', ar: 'أدخل التقسيم يدوياً' },
  'both.split.manual.paid': { en: 'Paid %', ar: 'نسبة المدفوع ٪' },
  'both.split.manual.social': { en: 'Social %', ar: 'نسبة الاجتماعي ٪' },
  'both.split.manual.sumError': {
    en: 'Paid % and Social % must add up to 100.',
    ar: 'يجب أن يساوي مجموع نسبتي المدفوع والاجتماعي 100.',
  },

  'door.denied.title': { en: 'Paid ads are not available', ar: 'الإعلانات المدفوعة غير متاحة' },
  'door.denied.upsell': {
    en: 'Upgrade your plan to include paid ads, or contact the platform administrator if you believe this is a mistake.',
    ar: 'رقِّ خطتك لتشمل الإعلانات المدفوعة، أو تواصل مع مسؤول المنصة إذا كنت تعتقد أن هذا خطأ.',
  },
  'door.staged.pendingTitle': {
    en: 'Drafts staged — pending provider approval',
    ar: 'حُفظت المسودات — بانتظار موافقة المزوّد',
  },
  'door.staged.pendingBody': {
    en: 'Live delivery waits on Meta/Google app review. Nothing is faked — the rows stay drafts until approval and launch wiring land.',
    ar: 'التسليم الفعلي بانتظار مراجعة ميتا/غوغل. لا شيء يُزيَّف — تبقى السجلات مسودات حتى الموافقة واكتمال التفعيل.',
  },
  'door.staged.readyBody': {
    en: 'Drafts staged. Launch wiring ships after strategy F6.',
    ar: 'حُفظت المسودات. التفعيل يأتي بعد استراتيجية F6.',
  },

  'door.submit.paid': { en: 'Stage paid draft', ar: 'حفظ مسودة مدفوعة' },
  'door.submit.social': { en: 'Continue to social publishing', ar: 'المتابعة إلى النشر الاجتماعي' },
  'door.submit.both': { en: 'Stage both drafts', ar: 'حفظ مسودتي الجانبين' },
  'door.submit.staging': { en: 'Staging…', ar: 'جارٍ الحفظ…' },
  'door.cancel': { en: 'Cancel', ar: 'إلغاء' },
  'door.error.generic': { en: 'Something went wrong', ar: 'حدث خطأ ما' },
} as const

export type BroadcastCopyKey = keyof typeof BROADCAST_COPY

export function bcp(key: BroadcastCopyKey, locale: BroadcastLocale, vars?: Record<string, string | number>): string {
  return t(BROADCAST_COPY[key], locale, vars)
}

/** Micros → major units, whole numbers when exact (helper text only). */
export function formatMicros(micros: number): string {
  return Number.isInteger(micros / 1_000_000) ? String(micros / 1_000_000) : (micros / 1_000_000).toFixed(2)
}
